import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { apiService } from '../services/apiService';
import { ReportTemplate, SearchParameterType, TemplateSection } from '../types';
import { useToast } from '../contexts/ToastContext';
import { TemplateBuilder } from '../components/admin/TemplateBuilder';
import { MasterDataImportExport } from '../components/admin/MasterDataImportExport';

export const AdminReportTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ReportTemplate | null>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [parameterTypeId, setParameterTypeId] = useState<number | undefined>();
  const [showGenerator, setShowGenerator] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [generating, setGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState<any>(null);
  const [parameterTypes, setParameterTypes] = useState<SearchParameterType[]>([]);
  const [selectedParameterType, setSelectedParameterType] = useState<number | undefined>();
  const [groupByApi, setGroupByApi] = useState(true);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [useApiResponsesFallback, setUseApiResponsesFallback] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState<ReportTemplate | null>(null);
  const [showConfigJsonModal, setShowConfigJsonModal] = useState(false);
  const [configJsonTemplateId, setConfigJsonTemplateId] = useState<number | null>(null);
  const [configJsonText, setConfigJsonText] = useState('');
  const [configJsonLoading, setConfigJsonLoading] = useState(false);
  const [configJsonError, setConfigJsonError] = useState<string | null>(null);
  const configJsonFileInputRef = React.useRef<HTMLInputElement>(null);
  const { showToast } = useToast();
  const [expandedPreviewApis, setExpandedPreviewApis] = useState<Record<string, boolean>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalCount, setTotalCount] = useState(0);
  const [filterParameterType, setFilterParameterType] = useState<number | undefined>();
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'default'>('all');
  const [sortBy, setSortBy] = useState<'name_asc' | 'name_desc' | 'sections_desc' | 'fields_desc'>('name_asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [allTemplatesForSelect, setAllTemplatesForSelect] = useState<ReportTemplate[]>([]);
  const [previewSearchQuery, setPreviewSearchQuery] = useState('');
  const [previewTab, setPreviewTab] = useState<'visual' | 'json'>('visual');
  const [previewCollapsedSections, setPreviewCollapsedSections] = useState<Record<number, boolean>>({});

  const fetchTemplates = async (page = currentPage) => {
    try {
      setLoading(true);
      const response = await apiService.getReportTemplates({
        page,
        page_size: pageSize,
        parameter_type: filterParameterType,
        is_active: filterStatus === 'active' ? true : filterStatus === 'inactive' ? false : undefined,
        search: searchQuery.trim() || undefined,
      });
      setTemplates(response.results || []);
      setTotalCount(response.count || 0);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch templates');
      showToast('Failed to load report templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTemplatesForSelect = async () => {
    try {
      const response = await apiService.getReportTemplates({ page: 1, page_size: 200 });
      setAllTemplatesForSelect(response.results || []);
    } catch (err) {
      console.error('Failed to load templates for select:', err);
    }
  };

  useEffect(() => {
    fetchTemplates(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filterParameterType, filterStatus]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchTemplates(1);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const types = await apiService.getParameterTypes();
        setParameterTypes(types);
      } catch (err) {
        console.error('Failed to load parameter types:', err);
      }
      const urlParams = new URLSearchParams(window.location.search);
      const searchId = urlParams.get('createFromSearch');
      if (searchId) {
        loadSearchResults(Number(searchId));
      }
    };
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const loadSearchResults = async (searchId: number) => {
    try {
      const report = await apiService.getSearchReport(searchId);
      if (report.results) {
        setSearchResults(report.results);
        // Try to find parameter type from search
        const paramTypes = await apiService.getParameterTypes();
        const found = paramTypes.find(pt => pt.display_name === report.parameter_type);
        if (found) {
          setParameterTypeId(found.id);
        }
        setShowBuilder(true);
      }
    } catch (err: any) {
      showToast('Failed to load search results', 'error');
    }
  };

  const handleSetDefault = async (templateId: number) => {
    try {
      await apiService.setDefaultTemplate(templateId);
      showToast('Template set as default successfully', 'success');
      fetchTemplates();
    } catch (err: any) {
      // Extract error message from API response
      const errorMessage = err?.message || 
                          err?.details?.error || 
                          err?.error || 
                          'Failed to set default template';
      showToast(errorMessage, 'error');
      console.error('Error setting default template:', err);
    }
  };

  const handleDelete = async (templateId: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    try {
      await apiService.deleteReportTemplate(templateId);
      showToast('Template deleted successfully', 'success');
      fetchTemplates();
    } catch (err: any) {
      showToast('Failed to delete template', 'error');
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setShowBuilder(true);
  };

  const handleEditTemplate = async (template: ReportTemplate) => {
    try {
      // Fetch full template details including configuration
      if (template.id) {
        const fullTemplate = await apiService.getReportTemplate(template.id);
        setEditingTemplate(fullTemplate);
        setShowBuilder(true);
      } else {
        setEditingTemplate(template);
        setShowBuilder(true);
      }
    } catch (err: any) {
      showToast('Failed to load template details', 'error');
      console.error('Error loading template:', err);
    }
  };

  const handleSaveTemplate = async (templateData: Partial<ReportTemplate>) => {
    try {
      if (editingTemplate?.id) {
        await apiService.updateReportTemplate(editingTemplate.id, templateData);
        // PATCH response can be partial; reload full template to keep editor state complete.
        const refreshedTemplate = await apiService.getReportTemplate(editingTemplate.id);
        setEditingTemplate(refreshedTemplate);
        showToast('Template updated successfully', 'success');
      } else {
        const created = await apiService.createReportTemplate(templateData);
        if (created?.id) {
          const refreshedTemplate = await apiService.getReportTemplate(created.id);
          setEditingTemplate(refreshedTemplate);
        } else {
          setEditingTemplate(created);
        }
        showToast('Template created successfully', 'success');
      }
      fetchTemplates();
    } catch (err: any) {
      throw new Error(err.message || 'Failed to save template');
    }
  };

  const handleCancelBuilder = () => {
    setShowBuilder(false);
    setEditingTemplate(null);
    setSearchResults([]);
    setParameterTypeId(undefined);
    // Remove query params
    window.history.replaceState({}, '', window.location.pathname);
  };

  const handlePreviewGeneration = async () => {
    try {
      const preview = await apiService.previewTemplateGeneration({
        parameter_type_id: selectedParameterType,
        group_by_api: groupByApi,
        use_api_responses_fallback: useApiResponsesFallback,
      });
      setPreviewData(preview);
    } catch (err: any) {
      showToast('Failed to load preview', 'error');
      console.error('Preview error:', err);
    }
  };

  const handleGenerateTemplates = async () => {
    try {
      setGenerating(true);
      const result = await apiService.generateTemplatesFromDisplayParameters({
        parameter_type_id: selectedParameterType,
        group_by_api: groupByApi,
        overwrite_existing: overwriteExisting,
        merge_strategy: 'first',
        use_api_responses_fallback: useApiResponsesFallback,
      });
      
      setGenerationResult(result);
      
      if (result.summary.created > 0 || result.summary.updated > 0) {
        showToast(
          `Successfully created ${result.summary.created} and updated ${result.summary.updated} templates`,
          'success'
        );
        fetchTemplates();
      }
      
      if (result.summary.errors > 0) {
        showToast(`${result.summary.errors} errors occurred during generation`, 'warning');
      }
    } catch (err: any) {
      showToast('Failed to generate templates: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handlePreviewTemplate = async (template: ReportTemplate) => {
    try {
      // Fetch full template if needed
      if (template.id && !template.configuration) {
        const fullTemplate = await apiService.getReportTemplate(template.id);
        setPreviewTemplate(fullTemplate);
      } else {
        setPreviewTemplate(template);
      }
      setPreviewSearchQuery('');
      setPreviewTab('visual');
      setPreviewCollapsedSections({});
    } catch (err: any) {
      showToast('Failed to load template for preview', 'error');
    }
  };

  const handleToggleActive = async (template: ReportTemplate) => {
    if (!template.id) return;
    try {
      await apiService.updateReportTemplate(template.id, { is_active: !template.is_active });
      showToast(`Template "${template.name}" ${!template.is_active ? 'activated' : 'deactivated'}`, 'success');
      fetchTemplates();
    } catch (err: any) {
      showToast(err.message || 'Failed to update template status', 'error');
    }
  };

  const handleDuplicateTemplate = async (template: ReportTemplate) => {
    if (!template.id) return;
    try {
      const full = await apiService.getReportTemplate(template.id);
      const ptId = typeof full.parameter_type === 'number'
        ? full.parameter_type
        : (full.parameter_type as any)?.id || (full as any).parameter_type_id;
      const copyData: Partial<ReportTemplate> = {
        name: `${full.name} (Copy)`,
        description: full.description ? `${full.description} (Copy)` : '',
        parameter_type: ptId,
        configuration: full.configuration,
        is_default: false,
        is_active: true,
      };
      await apiService.createReportTemplate(copyData);
      showToast(`Template duplicated as "${copyData.name}"`, 'success');
      fetchTemplates();
    } catch (err: any) {
      showToast(err.message || 'Failed to duplicate template', 'error');
    }
  };

  const hasActiveFilters = Boolean(searchQuery.trim() || filterParameterType !== undefined || filterStatus !== 'all');

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterParameterType(undefined);
    setFilterStatus('all');
    setCurrentPage(1);
  };

  const sortedTemplates = React.useMemo(() => {
    let list = [...templates];
    if (filterStatus === 'default') {
      list = list.filter((t) => t.is_default);
    }
    list.sort((a, b) => {
      if (sortBy === 'name_asc') return a.name.localeCompare(b.name);
      if (sortBy === 'name_desc') return b.name.localeCompare(a.name);
      if (sortBy === 'sections_desc') {
        const aSec = a.section_count ?? a.configuration?.sections?.length ?? 0;
        const bSec = b.section_count ?? b.configuration?.sections?.length ?? 0;
        return bSec - aSec;
      }
      if (sortBy === 'fields_desc') {
        const aF = a.field_count ?? a.configuration?.sections?.reduce((sum: number, s: TemplateSection) => sum + (s.fields?.length || 0), 0) ?? 0;
        const bF = b.field_count ?? b.configuration?.sections?.reduce((sum: number, s: TemplateSection) => sum + (s.fields?.length || 0), 0) ?? 0;
        return bF - aF;
      }
      return 0;
    });
    return list;
  }, [templates, filterStatus, sortBy]);

  const jsonValidationStatus = React.useMemo(() => {
    if (!configJsonText.trim()) return null;
    try {
      const parsed = JSON.parse(configJsonText);
      if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.sections)) {
        return { valid: false, message: 'Missing or invalid "sections" array' };
      }
      const totalSections = parsed.sections.length;
      const totalFields = parsed.sections.reduce(
        (sum: number, s: any) => sum + (Array.isArray(s?.fields) ? s.fields.length : 0),
        0
      );
      return { valid: true, message: `Valid JSON (${totalSections} sections, ${totalFields} fields)` };
    } catch (e: any) {
      return { valid: false, message: e.message || 'Invalid JSON syntax' };
    }
  }, [configJsonText]);

  if (showBuilder) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={editingTemplate ? 'Edit Template' : 'Create Template'}
          subtitle={editingTemplate ? `Editing "${editingTemplate.name}"` : 'Define sections and fields for your report template'}
        >
          <Button variant="outline" onClick={handleCancelBuilder}>
            Back to Templates
          </Button>
        </PageHeader>
        <TemplateBuilder
          template={editingTemplate || undefined}
          parameterTypeId={parameterTypeId}
          apiResults={searchResults}
          onSave={handleSaveTemplate}
          onCancel={handleCancelBuilder}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[320px]">
        <LoadingSpinner size="lg" text="Loading templates..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Report Templates" subtitle="Manage report templates" />
        <Card className="border-red-200 dark:border-red-900/50">
          <div className="p-8 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Button onClick={() => fetchTemplates(currentPage)}>Retry</Button>
          </div>
        </Card>
      </div>
    );
  }

  const openConfigJsonModal = async (templateId?: number) => {
    setShowConfigJsonModal(true);
    setConfigJsonError(null);
    await fetchAllTemplatesForSelect();
    const id = templateId ?? templates[0]?.id ?? allTemplatesForSelect[0]?.id ?? null;
    setConfigJsonTemplateId(id);
    if (id) {
      setConfigJsonLoading(true);
      try {
        const full = await apiService.getReportTemplate(id);
        const config = full.configuration ?? { sections: [] };
        setConfigJsonText(JSON.stringify(config, null, 2));
      } catch (e: any) {
        setConfigJsonError(e?.message || 'Failed to load template');
        setConfigJsonText('');
      } finally {
        setConfigJsonLoading(false);
      }
    } else {
      setConfigJsonText('');
    }
  };

  const loadConfigJsonForTemplate = async (templateId: number) => {
    setConfigJsonTemplateId(templateId);
    setConfigJsonError(null);
    setConfigJsonLoading(true);
    try {
      const full = await apiService.getReportTemplate(templateId);
      const config = full.configuration ?? { sections: [] };
      setConfigJsonText(JSON.stringify(config, null, 2));
    } catch (e: any) {
      setConfigJsonError(e?.message || 'Failed to load template');
      setConfigJsonText('');
    } finally {
      setConfigJsonLoading(false);
    }
  };

  const saveConfigJson = async () => {
    if (configJsonTemplateId == null) {
      showToast('Select a template first', 'error');
      return;
    }
    let parsed: { sections?: unknown[] };
    try {
      parsed = JSON.parse(configJsonText);
    } catch (e: any) {
      setConfigJsonError(e?.message || 'Invalid JSON');
      showToast('Invalid JSON', 'error');
      return;
    }
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.sections)) {
      setConfigJsonError('Configuration must have a "sections" array');
      showToast('Configuration must have a "sections" array', 'error');
      return;
    }
    setConfigJsonError(null);
    setConfigJsonLoading(true);
    const configuration = parsed as ReportTemplate['configuration'];
    try {
      await apiService.updateReportTemplate(configJsonTemplateId, { configuration });
      showToast('Configuration saved', 'success');
      fetchTemplates();
    } catch (e: any) {
      setConfigJsonError(e?.message || 'Failed to save');
      showToast(e?.message || 'Failed to save configuration', 'error');
    } finally {
      setConfigJsonLoading(false);
    }
  };

  const downloadConfigJson = () => {
    if (!configJsonText.trim()) return;
    const template = templates.find(t => t.id === configJsonTemplateId);
    const name = template ? `${template.name.replace(/\s+/g, '-')}-configuration.json` : 'report-template-configuration.json';
    const blob = new Blob([configJsonText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Download started', 'success');
  };

  const uploadConfigJson = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result);
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === 'object') {
          setConfigJsonText(JSON.stringify(parsed, null, 2));
          setConfigJsonError(null);
          setConfigJsonLoading(false);
          showToast('File loaded. Edit if needed, then click Save to database.', 'success');
        } else {
          setConfigJsonError('File must contain a JSON object');
          setConfigJsonLoading(false);
        }
      } catch (err: any) {
        setConfigJsonError(err?.message || 'Invalid JSON file');
        setConfigJsonLoading(false);
        showToast('Invalid JSON file', 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const prettifyConfigJson = () => {
    if (!configJsonText.trim()) return;
    try {
      const parsed = JSON.parse(configJsonText);
      setConfigJsonText(JSON.stringify(parsed, null, 2));
      setConfigJsonError(null);
      showToast('JSON formatted successfully', 'success');
    } catch (e: any) {
      setConfigJsonError(e?.message || 'Invalid JSON syntax');
    }
  };

  const copyConfigJson = () => {
    if (!configJsonText.trim()) return;
    navigator.clipboard.writeText(configJsonText);
    showToast('Configuration copied to clipboard', 'success');
  };

  const togglePreviewSectionCollapse = (index: number) => {
    setPreviewCollapsedSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="space-y-8">
      <PageHeader
        title="Report Templates"
        subtitle="Manage report templates to organize search results into structured sections"
      >
        <div className="flex flex-wrap gap-2.5 items-center">
          <Button
            onClick={handleCreateTemplate}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Template
          </Button>
          <Button
            variant={showGenerator ? 'default' : 'outline'}
            onClick={() => {
              setShowGenerator(!showGenerator);
              if (!showGenerator && !previewData) {
                handlePreviewGeneration();
              }
            }}
            className="gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {showGenerator ? 'Close Generator' : 'Auto-Generate'}
          </Button>
          <Button
            variant="outline"
            onClick={() => openConfigJsonModal()}
            disabled={totalCount === 0}
            className="gap-2"
            title="View and edit configuration JSON"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            Configuration JSON
          </Button>
          <MasterDataImportExport resource="report_templates" onComplete={fetchTemplates} />
        </div>
      </PageHeader>

      {!showBuilder && !loading && (
        <div className="flex flex-col gap-3 px-1">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-sm">
                <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{totalCount}</span>
                <span className="text-gray-500 dark:text-gray-400">template{totalCount !== 1 ? 's' : ''}</span>
              </div>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium underline flex items-center gap-1"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Reset Filters
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              {/* Search input with clear button */}
              <div className="relative min-w-[190px] flex-1 sm:flex-initial">
                <svg
                  className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                  placeholder="Search templates..."
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5"
                    title="Clear search"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Parameter type filter */}
              <select
                value={filterParameterType ?? ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const val = e.target.value;
                  setFilterParameterType(val ? Number(val) : undefined);
                  setCurrentPage(1);
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All parameter types</option>
                {parameterTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>{pt.display_name}</option>
                ))}
              </select>

              {/* Status filter */}
              <select
                value={filterStatus}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setFilterStatus(e.target.value as any);
                  setCurrentPage(1);
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
                <option value="default">Default Only</option>
              </select>

              {/* Sort by */}
              <select
                value={sortBy}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSortBy(e.target.value as any)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="name_asc">Name (A-Z)</option>
                <option value="name_desc">Name (Z-A)</option>
                <option value="sections_desc">Most Sections</option>
                <option value="fields_desc">Most Fields</option>
              </select>

              {/* Page size */}
              <select
                value={pageSize}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value={6}>6 per page</option>
                <option value={12}>12 per page</option>
                <option value={24}>24 per page</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Auto-Generation Section */}
      {showGenerator && (
        <Card className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-slate-50 to-indigo-50/30 dark:from-gray-800 dark:to-indigo-950/20">
          <div className="p-6 sm:p-8 space-y-6">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Auto-Generate from Display Parameters
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 max-w-xl">
                  Create templates for each parameter type using all fields from stored search API responses, merged with display parameters. One section per API.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Parameter Type (Optional)
                </label>
                <select
                  value={selectedParameterType || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const val = e.target.value;
                    setSelectedParameterType(val ? Number(val) : undefined);
                    setPreviewData(null);
                  }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors shadow-sm"
                >
                  <option value="">All Parameter Types</option>
                  {parameterTypes.map((pt) => (
                    <option key={pt.id} value={pt.id}>
                      {pt.display_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col justify-end space-y-3">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={groupByApi}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setGroupByApi(e.target.checked);
                      setPreviewData(null);
                    }}
                    className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                    Group by API (separate sections per API)
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={overwriteExisting}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOverwriteExisting(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                    Overwrite existing auto-generated templates
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={useApiResponsesFallback}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setUseApiResponsesFallback(e.target.checked);
                      setPreviewData(null);
                    }}
                    className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                    Include all fields from stored search API responses (merged with display parameters)
                  </span>
                </label>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-1">
              <Button
                variant="outline"
                onClick={handlePreviewGeneration}
                disabled={generating}
                className="gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Preview
              </Button>
              <Button
                onClick={handleGenerateTemplates}
                disabled={generating}
                className="gap-2"
              >
                {generating ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Generate Templates
                  </>
                )}
              </Button>
            </div>

            {previewData && (
              <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-4 sm:p-5 bg-white/60 dark:bg-gray-800/40 backdrop-blur-sm">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Preview
                </h3>
                <div className="flex flex-wrap gap-4 text-sm mb-4">
                  <span className="px-3 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                    {previewData.summary.parameter_types} parameter types
                  </span>
                  <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    {previewData.summary.total_apis} APIs
                  </span>
                  <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    {previewData.summary.total_fields} fields
                  </span>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto custom-scrollbar">
                  {previewData.preview.map((item: any) => (
                    <div
                      key={item.parameter_type_id}
                      className="p-3 bg-white dark:bg-gray-700/80 rounded-lg border border-gray-100 dark:border-gray-600/50 space-y-2"
                    >
                      <div className="font-medium text-gray-900 dark:text-white">
                        {item.parameter_type_name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {item.apis_count} APIs · {item.total_fields} fields
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {item.apis.map((api: any) => (
                          <span
                            key={api.api_id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-600 rounded text-xs"
                          >
                            {api.section_heading || api.api_name} ({api.fields_count})
                            {api.sample_response && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const key = `${item.parameter_type_id}-${api.api_id}`;
                                    setExpandedPreviewApis((prev) => ({
                                      ...prev,
                                      [key]: !prev[key],
                                    }));
                                  }}
                                  className="ml-1 text-[10px] px-1 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200 hover:bg-indigo-200 dark:hover:bg-indigo-800"
                                >
                                  JSON
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const jsonText = JSON.stringify(api.sample_response, null, 2);
                                    const blob = new Blob([jsonText], { type: 'application/json' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    const safeName = api.api_name
                                      ? api.api_name.replace(/\s+/g, '-')
                                      : `api-${api.api_id}`;
                                    a.download = `${safeName}-sample-response.json`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                    showToast('Download started', 'success');
                                  }}
                                  className="text-[10px] px-1 py-0.5 rounded bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
                                  title="Download sample JSON"
                                >
                                  DL
                                </button>
                              </>
                            )}
                          </span>
                        ))}
                      </div>
                      {/* Sample JSON preview for APIs under this parameter type */}
                      {item.apis.map((api: any) => {
                        const key = `${item.parameter_type_id}-${api.api_id}`;
                        if (!api.sample_response || !expandedPreviewApis[key]) {
                          return null;
                        }
                        const jsonText = JSON.stringify(api.sample_response, null, 2);
                        return (
                          <div
                            key={key}
                            className="mt-2 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/70 p-2 space-y-1"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {api.api_name} – Sample JSON response
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard
                                      .writeText(jsonText)
                                      .then(() => showToast('JSON copied to clipboard', 'success'))
                                      .catch(() =>
                                        showToast('Failed to copy JSON to clipboard', 'error')
                                      );
                                  }}
                                  className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                                >
                                  Copy
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const blob = new Blob([jsonText], {
                                      type: 'application/json',
                                    });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    const safeName = api.api_name
                                      ? api.api_name.replace(/\s+/g, '-')
                                      : `api-${api.api_id}`;
                                    a.download = `${safeName}-sample-response.json`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                    showToast('Download started', 'success');
                                  }}
                                  className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                                >
                                  Download
                                </button>
                              </div>
                            </div>
                            <textarea
                              readOnly
                              value={jsonText}
                              className="w-full mt-1 text-[11px] font-mono border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-950 text-gray-800 dark:text-gray-100 p-2 resize-y min-h-[120px]"
                              spellCheck={false}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {generationResult && (
              <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-4 sm:p-5 bg-white/60 dark:bg-gray-800/40">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Generation Results</h3>
                <div className="flex flex-wrap gap-3 mb-3">
                  <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-sm">
                    Created: {generationResult.summary.created}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-sm">
                    Updated: {generationResult.summary.updated}
                  </span>
                  {generationResult.summary.errors > 0 && (
                    <span className="px-3 py-1 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm">
                      Errors: {generationResult.summary.errors}
                    </span>
                  )}
                </div>
                {(generationResult.created_templates?.length ?? 0) > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Created Templates</p>
                    <ul className="space-y-1.5">
                      {(generationResult.created_templates ?? []).map((t: any) => (
                        <li key={t.id} className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                          <svg className="w-3.5 h-3.5 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {t.name} ({t.sections_count} sections, {t.fields_count} fields)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(generationResult.errors?.length ?? 0) > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-2">Errors</p>
                    <ul className="space-y-1">
                      {(generationResult.errors ?? []).map((e: any, idx: number) => (
                        <li key={idx} className="text-sm text-red-600 dark:text-red-400">
                          {e.parameter_type}: {e.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {templates.length === 0 && !loading && totalCount === 0 ? (
        <Card className="border-2 border-dashed border-gray-200 dark:border-gray-600 overflow-hidden">
          <div className="p-12 sm:p-16 text-center">
            <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-100 to-indigo-50 dark:from-indigo-900/30 dark:to-indigo-950/30 flex items-center justify-center mb-6 shadow-inner">
              <svg className="w-10 h-10 text-indigo-500 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No templates yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto leading-relaxed">
              Create your first report template to structure how search results are displayed, or auto-generate from display parameters.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={handleCreateTemplate} size="lg" className="gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create First Template
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  setShowGenerator(true);
                  handlePreviewGeneration();
                }}
                className="gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Auto-Generate
              </Button>
            </div>
          </div>
        </Card>
      ) : templates.length === 0 && !loading ? (
        <Card>
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No templates match your filters. Try clearing search or parameter type filter.
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
          {sortedTemplates.map((template) => {
            const sectionCount = template.section_count ?? template.configuration?.sections?.length ?? 0;
            const fieldCount = template.field_count ?? template.configuration?.sections?.reduce(
              (sum: number, s: TemplateSection) => sum + (s.fields?.length || 0),
              0
            ) ?? 0;
            return (
              <Card
                key={template.id}
                className="group hover:border-indigo-300 dark:hover:border-indigo-600/50 hover:shadow-md transition-all duration-200 flex flex-col justify-between"
              >
                <div className="p-5 sm:p-6 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate" title={template.name}>
                          {template.name}
                        </h3>
                        {template.is_default && (
                          <Badge variant="success" size="sm" className="gap-1 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300">
                            <svg className="w-3 h-3 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            Default
                          </Badge>
                        )}
                        <button
                          type="button"
                          onClick={() => handleToggleActive(template)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                            template.is_active
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 hover:bg-emerald-100'
                              : 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 hover:bg-gray-200'
                          }`}
                          title={`Click to ${template.is_active ? 'deactivate' : 'activate'} template`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${template.is_active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                          {template.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                      {template.description ? (
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                          {template.description}
                        </p>
                      ) : (
                        <p className="text-xs italic text-gray-400 dark:text-gray-500">
                          No description provided
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 text-xs font-medium text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/50">
                      <svg className="w-3.5 h-3.5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      {template.parameter_type_name ?? template.parameter_type_detail?.display_name ?? 'N/A'}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-700/80 text-xs text-gray-600 dark:text-gray-300">
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      {sectionCount} section{sectionCount !== 1 ? 's' : ''}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-700/80 text-xs text-gray-600 dark:text-gray-300">
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      {fieldCount} field{fieldCount !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleEditTemplate(template)}
                        className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreviewTemplate(template)}
                        className="gap-1.5"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Preview
                      </Button>
                    </div>

                    <div className="flex items-center gap-1.5">
                      {!template.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => template.id && handleSetDefault(template.id)}
                          className="gap-1 text-xs text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                          title="Set as default template for this search type"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Set Default
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDuplicateTemplate(template)}
                        className="p-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                        title="Duplicate template"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => template.id && handleDelete(template.id)}
                        className="p-1.5 text-red-600 hover:text-red-700 hover:border-red-300 dark:text-red-400 dark:hover:text-red-300 dark:hover:border-red-500/50"
                        title="Delete template"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="flex justify-between items-start gap-4 p-5 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-indigo-50/30 dark:from-gray-800 dark:to-indigo-950/20">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                    {previewTemplate.name}
                  </h2>
                  {previewTemplate.is_default ? (
                    <Badge variant="default" className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
                      Default Template
                    </Badge>
                  ) : previewTemplate.is_active ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="default">Inactive</Badge>
                  )}
                  {previewTemplate.parameter_type_detail && (
                    <Badge variant="outline" className="text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800">
                      {previewTemplate.parameter_type_detail.display_name}
                    </Badge>
                  )}
                </div>
                {previewTemplate.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {previewTemplate.description}
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreviewTemplate(null)}
                className="shrink-0"
              >
                Close
              </Button>
            </div>

            {/* Template Summary Banner */}
            <div className="bg-indigo-50/60 dark:bg-indigo-950/30 border-b border-indigo-100 dark:border-indigo-900/50 px-6 py-3 flex flex-wrap items-center justify-between gap-4 text-xs">
              <div className="flex flex-wrap items-center gap-4 text-gray-600 dark:text-gray-300">
                <span>
                  <strong className="text-gray-900 dark:text-white font-semibold">
                    {previewTemplate.configuration?.sections?.length || 0}
                  </strong>{' '}
                  Sections
                </span>
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <span>
                  <strong className="text-gray-900 dark:text-white font-semibold">
                    {previewTemplate.configuration?.sections?.reduce(
                      (sum: number, s: TemplateSection) => sum + (s.fields?.length || 0),
                      0
                    ) || 0}
                  </strong>{' '}
                  Total Fields
                </span>
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <span>
                  <strong className="text-gray-900 dark:text-white font-semibold">
                    {previewTemplate.configuration?.sections?.filter(s => s.collapsible).length || 0}
                  </strong>{' '}
                  Collapsible
                </span>
              </div>

              {/* Tab Switcher */}
              <div className="flex items-center gap-1 bg-white dark:bg-gray-700/80 p-0.5 rounded-lg border border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => setPreviewTab('visual')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    previewTab === 'visual'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Visual View
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewTab('json')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    previewTab === 'json'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Raw JSON
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
              {previewTab === 'visual' ? (
                <div className="space-y-4">
                  {/* Search and collapse controls */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pb-2">
                    <div className="relative w-full sm:w-80">
                      <input
                        type="text"
                        value={previewSearchQuery}
                        onChange={(e) => setPreviewSearchQuery(e.target.value)}
                        placeholder="Search sections or fields..."
                        className="w-full pl-9 pr-8 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-750 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <svg className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      {previewSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setPreviewSearchQuery('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const allCollapsed: Record<number, boolean> = {};
                          previewTemplate.configuration?.sections?.forEach((_, idx) => {
                            allCollapsed[idx] = false;
                          });
                          setPreviewCollapsedSections(allCollapsed);
                        }}
                        className="text-xs px-2.5 py-1"
                      >
                        Expand All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const allCollapsed: Record<number, boolean> = {};
                          previewTemplate.configuration?.sections?.forEach((s, idx) => {
                            if (s.collapsible) allCollapsed[idx] = true;
                          });
                          setPreviewCollapsedSections(allCollapsed);
                        }}
                        className="text-xs px-2.5 py-1"
                      >
                        Collapse All
                      </Button>
                    </div>
                  </div>

                  {/* Sections List */}
                  {previewTemplate.configuration?.sections && previewTemplate.configuration.sections.length > 0 ? (
                    <div className="space-y-4">
                      {previewTemplate.configuration.sections
                        .map((section: TemplateSection, sectionIndex: number) => {
                          const q = previewSearchQuery.trim().toLowerCase();
                          const matchesHeading = !q || (section.heading?.toLowerCase().includes(q) ?? false);
                          const matchingFields = (section.fields || [])
                            .filter((f: { hidden_in_report?: boolean }) => !f.hidden_in_report)
                            .filter((f: any) => {
                              if (!q || matchesHeading) return true;
                              return (
                                f.display_name?.toLowerCase().includes(q) ||
                                f.field_path?.toLowerCase().includes(q) ||
                                f.api_source?.toLowerCase().includes(q)
                              );
                            });
                          
                          if (q && !matchesHeading && matchingFields.length === 0) {
                            return null;
                          }

                          const isCollapsed = Boolean(section.collapsible && previewCollapsedSections[sectionIndex]);

                          return (
                            <Card key={sectionIndex} className="overflow-hidden border border-gray-200 dark:border-gray-700">
                              <div
                                className={`p-4 flex items-center justify-between select-none ${
                                  section.collapsible ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750/50' : ''
                                }`}
                                onClick={() => section.collapsible && togglePreviewSectionCollapse(sectionIndex)}
                              >
                                <div className="flex items-center gap-2.5">
                                  {section.collapsible && (
                                    <svg
                                      className={`w-4 h-4 text-gray-500 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  )}
                                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                    {section.heading || `Section ${sectionIndex + 1}`}
                                  </h3>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                    {matchingFields.length} {matchingFields.length === 1 ? 'field' : 'fields'}
                                  </span>
                                  {section.collapsible && (
                                    <Badge variant="default" className="text-xs">Collapsible</Badge>
                                  )}
                                </div>
                                <span className="text-xs text-gray-400 font-mono">
                                  Order: {section.order ?? sectionIndex + 1}
                                </span>
                              </div>

                              {!isCollapsed && (
                                <div className="p-4 pt-0 border-t border-gray-100 dark:border-gray-700/60">
                                  {matchingFields.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                      {matchingFields
                                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                                        .map((field, fieldIndex: number) => (
                                          <div
                                            key={fieldIndex}
                                            className="border-l-4 border-indigo-500 pl-3 py-2 bg-gray-50 dark:bg-gray-700/40 rounded-r-lg"
                                          >
                                            <div className="flex justify-between items-start gap-2">
                                              <div className="min-w-0 flex-1">
                                                <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                                  {field.display_name || 'Unnamed Field'}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 space-y-0.5">
                                                  <div className="flex items-center gap-1 truncate">
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">API:</span>
                                                    <span className="truncate">{field.api_source || 'N/A'}</span>
                                                  </div>
                                                  <div className="flex items-center gap-1 truncate font-mono text-[11px]">
                                                    <span className="font-sans font-medium text-gray-700 dark:text-gray-300">Path:</span>
                                                    <span className="truncate">{field.field_path || 'N/A'}</span>
                                                  </div>
                                                  {field.merge_strategy && (
                                                    <div className="flex items-center gap-1">
                                                      <span className="font-medium text-gray-700 dark:text-gray-300">Merge:</span>
                                                      <span className="capitalize">{field.merge_strategy}</span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="text-xs text-gray-400 italic shrink-0">
                                                Sample Value
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  ) : (
                                    <div className="text-xs text-gray-400 text-center py-4">
                                      {previewSearchQuery ? 'No fields in this section match the search query' : 'No fields configured'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </Card>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      No sections configured in this template
                    </div>
                  )}
                </div>
              ) : (
                /* Raw JSON tab */
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Raw template configuration schema:
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(previewTemplate.configuration, null, 2));
                        showToast('Configuration JSON copied to clipboard', 'success');
                      }}
                      className="text-xs gap-1.5"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy JSON
                    </Button>
                  </div>
                  <pre className="p-4 bg-gray-900 text-gray-100 rounded-xl font-mono text-xs overflow-x-auto max-h-[500px] custom-scrollbar border border-gray-800 leading-relaxed">
                    {JSON.stringify(previewTemplate.configuration, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Configuration JSON modal */}
      {showConfigJsonModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="flex justify-between items-center gap-4 p-5 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-indigo-50/30 dark:from-gray-800 dark:to-indigo-950/20">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Configuration JSON</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Directly inspect, edit, or migrate template JSON configurations
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowConfigJsonModal(false)}>Close</Button>
            </div>

            {/* Toolbar */}
            <div className="p-4 bg-gray-50/70 dark:bg-gray-750/50 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Template:</label>
                <select
                  value={configJsonTemplateId ?? ''}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : null;
                    if (id) loadConfigJsonForTemplate(id);
                    else {
                      setConfigJsonTemplateId(null);
                      setConfigJsonText('');
                      setConfigJsonError(null);
                    }
                  }}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="">Select template</option>
                  {(allTemplatesForSelect.length > 0 ? allTemplatesForSelect : templates).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prettifyConfigJson}
                  disabled={!configJsonText.trim()}
                  className="text-xs gap-1.5"
                  title="Format JSON with indentation"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
                  </svg>
                  Format JSON
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyConfigJson}
                  disabled={!configJsonText.trim()}
                  className="text-xs gap-1.5"
                  title="Copy JSON to clipboard"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadConfigJson}
                  disabled={!configJsonText.trim()}
                  className="text-xs gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => configJsonFileInputRef.current?.click()}
                  className="text-xs gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l4-4m0 0l4 4m-4-4v12" />
                  </svg>
                  Upload
                </Button>
                <input
                  ref={configJsonFileInputRef}
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  onChange={uploadConfigJson}
                />
              </div>
            </div>

            {/* Validation and Error Alerts */}
            {jsonValidationStatus && (
              <div
                className={`px-5 py-2 text-xs flex items-center justify-between border-b ${
                  jsonValidationStatus.valid
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/50'
                    : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/50'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  {jsonValidationStatus.valid ? (
                    <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  <span>{jsonValidationStatus.message}</span>
                </div>
              </div>
            )}

            {configJsonError && (
              <div className="px-5 py-2 text-xs bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-b border-red-200 dark:border-red-800/50">
                {configJsonError}
              </div>
            )}

            {/* Editor Area */}
            <div className="p-4 flex-1 min-h-0 flex flex-col">
              {configJsonLoading ? (
                <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">Loading…</div>
              ) : (
                <textarea
                  value={configJsonText}
                  onChange={(e) => {
                    setConfigJsonText(e.target.value);
                    setConfigJsonError(null);
                  }}
                  placeholder='{"sections": [...]}'
                  className="w-full flex-1 min-h-[300px] p-3.5 font-mono text-xs border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none leading-relaxed custom-scrollbar"
                  spellCheck={false}
                />
              )}
            </div>

            {/* Footer */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Changes will directly update the template structure in the database.
              </p>
              <div className="flex items-center gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowConfigJsonModal(false)}>Cancel</Button>
                <Button
                  onClick={saveConfigJson}
                  disabled={
                    configJsonTemplateId == null ||
                    !configJsonText.trim() ||
                    configJsonLoading ||
                    (jsonValidationStatus !== null && !jsonValidationStatus.valid)
                  }
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  Save to database
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

